import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

interface AuthFormData {
  email: string;
  password: string;
  username: string;
  role: 'traveler' | 'host';
  nationality?: string;
  agreedToTerms?: boolean;
}

export const useAuthForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [formData, setFormData] = useState<AuthFormData>({
    email: '',
    password: '',
    username: '',
    role: (searchParams.get('role') === 'host' ? 'host' : 'traveler'),
    nationality: '',
    agreedToTerms: false,
  });

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const errorDescriptionParam = searchParams.get('error_description');
    
    if (errorParam && errorDescriptionParam) {
      console.error('Auth error from redirect:', errorParam, errorDescriptionParam);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: errorDescriptionParam,
      });
    }
    
    if (searchParams.get('email_confirmed') === 'true') {
      toast({
        title: "Email Verified",
        description: "Your email has been successfully verified. You can now log in.",
      });
    }
  }, [searchParams, toast]);

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationError('');
  };

  const validateForm = (isSignUp: boolean) => {
    setValidationError('');
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setValidationError('Please enter a valid email address');
      return false;
    }
    
    if (!formData.password && isSignUp) {
      setValidationError('Password is required');
      return false;
    }

    if (isSignUp) {
      if (formData.password.length < 6) {
        setValidationError('Password must be at least 6 characters long');
        return false;
      }
      if (!/[A-Z]/.test(formData.password)) {
        setValidationError('Password must contain at least one uppercase letter');
        return false;
      }
      if (!/[0-9]/.test(formData.password)) {
        setValidationError('Password must contain at least one number');
        return false;
      }
      if (!formData.username.trim()) {
        setValidationError('Username is required');
        return false;
      }
      if (!formData.agreedToTerms) {
        setValidationError('You must agree to the Terms of Service, Privacy Policy, and User Content Policy');
        return false;
      }
    }

    return true;
  };

  const handleForgotPassword = async () => {
    if (!formData.email.trim()) {
      setValidationError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setValidationError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) throw error;

      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for a link to reset your password.",
      });

      setFormData(prev => ({ ...prev, password: '' }));
    } catch (error: any) {
      console.error('Password reset error:', error);
      setValidationError(error.message);
      toast({
        variant: "destructive",
        title: "Password Reset Failed",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, isSignUp: boolean) => {
    e.preventDefault();
    
    if (!validateForm(isSignUp)) {
      return;
    }

    setIsLoading(true);

    try {
      if (isSignUp) {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              username: formData.username,
              role: formData.role,
              nationality: formData.nationality,
              agreedToTerms: formData.agreedToTerms,
              termsAgreedAt: new Date().toISOString(),
            },
            emailRedirectTo: `${window.location.origin}/auth?email_confirmed=true`,
          },
        });

        if (signUpError) {
          if (signUpError.message.includes('User already registered')) {
            setValidationError('This email is already registered. Please try logging in instead.');
            return;
          }
          throw signUpError;
        }

        toast({
          title: "Success!",
          description: "Please check your email to verify your account. You must verify your email before you can log in.",
        });

        setFormData({
          email: '',
          password: '',
          username: '',
          role: 'traveler',
          nationality: '',
          agreedToTerms: false,
        });

        console.log('Signup successful - email verification sent');
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (signInError) {
          if (signInError.message.includes('Invalid login credentials') || 
              signInError.message.includes('invalid_credentials')) {
            setValidationError('Invalid email or password. Please check your credentials and try again.');
            return;
          }
          throw signInError;
        }

        setFormData({
          email: '',
          password: '',
          username: '',
          role: 'traveler',
        });

        await queryClient.invalidateQueries({ queryKey: ['session'] });

        toast({
          title: "Welcome back!",
          description: "You have successfully logged in.",
        });
        
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      console.error('Authentication error:', error);
      setValidationError(error.message);
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    isLoading,
    validationError,
    handleFormChange,
    handleSubmit,
    handleForgotPassword,
  };
};
